import React, { useState, useMemo } from 'react';
import { Wallet, Plus, AlertCircle, Ban } from 'lucide-react';
import Modal from '../shared/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useTravelCredits } from '../../hooks/useTravelCredits';
import { createTravelCredit, voidTravelCredit, creditBalance, CREDIT_STATUS } from '../../utils/travelCredits';

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500';
const lbl = 'block text-xs font-medium text-gray-700 mb-1';

const money = (v) => `A$${(parseFloat(v) || 0).toFixed(2)}`;

const fmtDate = (ts) => {
  if (!ts) return '';
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return isNaN(d) ? '' : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Travel credit held by a passenger — money the client already paid on an earlier trip whose
 * services were cancelled or deferred, carried forward against a future trip.
 *
 * The client can see what credit they hold; only STX can issue one. Applying it happens on the
 * trip form, not here, because it has to move in step with the trip's own total.
 */
export default function TravelCreditsCard({ clientId, passenger, trips = [], isSTX }) {
  const { userProfile } = useAuth();
  const { credits } = useTravelCredits(clientId);

  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [form, setForm]       = useState({ amountInclGST: '', gstRate: '0.1', reason: '', sourceTripIds: [] });
  const [voiding, setVoiding]   = useState(null);   // the credit being voided
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError]   = useState('');
  const [voidSaving, setVoidSaving] = useState(false);

  const handleVoid = async () => {
    setVoidError('');
    if (!voidReason.trim()) return setVoidError('A reason is required — it stays on the record.');
    setVoidSaving(true);
    try {
      await voidTravelCredit({ clientId, creditId: voiding.id, reason: voidReason, user: userProfile });
      setVoiding(null);
      setVoidReason('');
    } catch (e) {
      setVoidError(e.message || 'Could not void the credit.');
    } finally {
      setVoidSaving(false);
    }
  };

  const mine = useMemo(
    () => credits
      .filter(c => c.passengerId === passenger?.id)
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)),
    [credits, passenger?.id],
  );

  const balance = creditBalance(mine);

  // Trips this credit could have come from — this passenger's, most recent first.
  const passengerTrips = useMemo(() => {
    const uid = passenger?.userId;
    return trips
      .filter(t => (uid && t.travellerId === uid) ||
        (t.travellerName || '').toLowerCase().trim() ===
          [passenger?.firstName, passenger?.lastName].filter(Boolean).join(' ').toLowerCase().trim())
      .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
  }, [trips, passenger]);

  const toggleTrip = (id) => setForm(p => ({
    ...p,
    sourceTripIds: p.sourceTripIds.includes(id)
      ? p.sourceTripIds.filter(x => x !== id)
      : [...p.sourceTripIds, id],
  }));

  const resetForm = () => setForm({ amountInclGST: '', gstRate: '0.1', reason: '', sourceTripIds: [] });

  const handleAdd = async () => {
    setError('');
    const amount = parseFloat(form.amountInclGST) || 0;
    if (amount <= 0)          return setError('Enter the credit amount (GST inclusive).');
    if (!form.reason.trim())  return setError('A reason is required — it is the audit trail for this credit.');
    if (form.sourceTripIds.length === 0)
      return setError('Select the trip (or trips) this credit came from.');

    setSaving(true);
    try {
      await createTravelCredit({
        clientId,
        user: userProfile,
        data: {
          passengerId:   passenger.id,
          passengerName: [passenger.firstName, passenger.lastName].filter(Boolean).join(' '),
          amountInclGST: amount,
          gstRate:       parseFloat(form.gstRate),
          reason:        form.reason.trim(),
          sourceTripIds: form.sourceTripIds,
          sourceTripRefs: form.sourceTripIds
            .map(id => trips.find(t => t.id === id)?.tripRef)
            .filter(Boolean),
        },
      });
      setShowAdd(false);
      resetForm();
    } catch (e) {
      setError(e.message || 'Could not save the credit.');
    } finally {
      setSaving(false);
    }
  };

  // Nothing to show a client user when the passenger has never held credit.
  if (!isSTX && mine.length === 0) return null;

  return (
    <div className="mt-6 pt-5 border-t border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
          <Wallet size={14} aria-hidden="true" className="text-gray-600" />
          Travel credit
        </h3>
        {isSTX && (
          <button
            onClick={() => { resetForm(); setError(''); setShowAdd(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <Plus size={13} aria-hidden="true" /> Add credit
          </button>
        )}
      </div>

      {mine.length === 0 ? (
        <p className="text-sm text-gray-600">No travel credit held.</p>
      ) : (
        <>
          <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-xs text-emerald-800">Available balance</p>
            <p className="text-xl font-semibold text-emerald-900 tabular-nums">{money(balance)}</p>
            <p className="text-xs text-emerald-800 mt-0.5">
              Applied to a future trip from the trip form{isSTX ? '' : ' by STX'}.
            </p>
          </div>

          <div className="space-y-2">
            {mine.map(c => {
              const used = (parseFloat(c.amountInclGST) || 0) - (parseFloat(c.remainingInclGST) || 0);
              return (
                <div key={c.id} className={`border rounded-lg px-3 py-2.5 ${
                  c.status === CREDIT_STATUS.VOID ? 'border-gray-200 bg-gray-50' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={`text-sm ${c.status === CREDIT_STATUS.VOID ? 'text-gray-600 line-through' : 'text-gray-800'}`}>
                        {c.reason || 'Travel credit'}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Issued {fmtDate(c.createdAt)}
                        {c.createdByName ? ` by ${c.createdByName}` : ''}
                        {(c.sourceTripRefs || []).length > 0 && ` · from ${c.sourceTripRefs.join(', ')}`}
                        {(c.gstRate ?? 0.1) === 0 && ' · GST-free'}
                      </p>
                    </div>
                    <div className="text-right whitespace-nowrap flex items-start gap-2">
                      <div>
                        <p className={`text-sm font-medium tabular-nums ${
                          c.status === CREDIT_STATUS.VOID ? 'text-gray-600' : 'text-gray-900'}`}>
                          {c.status === CREDIT_STATUS.VOID ? '—' : money(c.remainingInclGST)}
                        </p>
                        <p className="text-xs text-gray-600">of {money(c.amountInclGST)}</p>
                      </div>
                      {isSTX && c.status !== CREDIT_STATUS.VOID && (
                        <button
                          onClick={() => { setVoiding(c); setVoidReason(''); setVoidError(''); }}
                          aria-label={`Void credit: ${c.reason || 'travel credit'}`}
                          title="Void this credit"
                          className="p-1.5 text-gray-600 hover:text-red-700 rounded"
                        >
                          <Ban size={14} aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </div>

                  {c.status === CREDIT_STATUS.EXHAUSTED && (
                    <p className="text-xs text-gray-600 mt-1.5">Fully used.</p>
                  )}

                  {c.status === CREDIT_STATUS.VOID && (
                    <p className="text-xs text-red-700 mt-1.5">
                      <strong>Voided</strong>
                      {c.voidedByName ? ` by ${c.voidedByName}` : ''}
                      {c.voidedAt ? ` on ${fmtDate(c.voidedAt)}` : ''}
                      {c.voidReason ? ` — ${c.voidReason}` : ''}
                    </p>
                  )}

                  {used > 0 && (c.applications || []).length > 0 && (
                    <ul className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
                      {c.applications.map((a, i) => (
                        <li key={`${a.tripId}-${i}`} className="text-xs text-gray-700 flex justify-between gap-3">
                          <span className="truncate">
                            Applied to {a.tripRef || a.tripId}
                            {a.appliedByName ? ` by ${a.appliedByName}` : ''}
                          </span>
                          <span className="tabular-nums whitespace-nowrap">&minus; {money(a.amountInclGST)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {voiding && (
        <Modal title="Void travel credit" onClose={() => setVoiding(null)}>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                Void <strong>{voiding.reason || 'this credit'}</strong>
                {' '}({money(voiding.amountInclGST)} issued)?
              </p>
              <p className="text-sm text-red-800 mt-2">
                {money(voiding.remainingInclGST)} remaining will be cancelled and can no longer be
                applied to any trip.
              </p>
              {(voiding.applications || []).length > 0 && (
                <p className="text-sm text-red-800 mt-2">
                  <strong>
                    {money((parseFloat(voiding.amountInclGST) || 0) - (parseFloat(voiding.remainingInclGST) || 0))}
                    {' '}already applied to {voiding.applications.map(a => a.tripRef || a.tripId).join(', ')} stays applied.
                  </strong>{' '}
                  Those trips may already have been invoiced. To undo that too, remove the credit from
                  the trip first, then void.
                </p>
              )}
            </div>

            <p className="text-sm text-gray-700">
              Voiding cannot be undone. If this was issued in error, void it and issue a corrected
              credit — both stay on the record.
            </p>

            <div>
              <label className={lbl} htmlFor="void-reason">Reason for voiding *</label>
              <textarea
                id="void-reason"
                rows={2}
                className={inp}
                placeholder="e.g. Entered as $500 in error — reissued as $50"
                value={voidReason}
                onChange={e => setVoidReason(e.target.value)}
              />
            </div>

            {voidError && (
              <div role="alert" className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={15} aria-hidden="true" className="text-red-600 mt-0.5 shrink-0" />
                <p className="text-sm text-red-700">{voidError}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setVoiding(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Cancel
              </button>
              <button
                onClick={handleVoid}
                disabled={voidSaving}
                className="px-5 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {voidSaving ? 'Voiding…' : 'Void credit'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="Add travel credit" onClose={() => setShowAdd(false)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Credit for <strong>{passenger.firstName} {passenger.lastName}</strong> from travel the
              client has already paid for. This does not change the value of the trip it came from —
              it is carried forward and reduces what a future trip bills.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl} htmlFor="credit-amount">Amount (incl. GST) *</label>
                <input
                  id="credit-amount"
                  type="number" min="0" step="0.01"
                  className={inp}
                  placeholder="0.00"
                  value={form.amountInclGST}
                  onChange={e => setForm(p => ({ ...p, amountInclGST: e.target.value }))}
                />
              </div>
              <div>
                <label className={lbl} htmlFor="credit-gst">GST treatment</label>
                <select
                  id="credit-gst"
                  className={inp}
                  value={form.gstRate}
                  onChange={e => setForm(p => ({ ...p, gstRate: e.target.value }))}
                >
                  <option value="0.1">Includes GST (10%)</option>
                  <option value="0">GST-free (e.g. international)</option>
                </select>
              </div>
            </div>

            <div>
              <label className={lbl} htmlFor="credit-reason">Reason *</label>
              <textarea
                id="credit-reason"
                rows={2}
                className={inp}
                placeholder="e.g. Flight cancelled by airline — credit held with Qantas"
                value={form.reason}
                onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
              />
            </div>

            <div>
              <label className={lbl}>Which trip is this credit for? *</label>
              {passengerTrips.length === 0 ? (
                <p className="text-sm text-gray-600">
                  No trips found for this passenger to link the credit to.
                </p>
              ) : (
                <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {passengerTrips.map(t => (
                    <label key={t.id} className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={form.sourceTripIds.includes(t.id)}
                        onChange={() => toggleTrip(t.id)}
                      />
                      <span className="text-sm text-gray-800">
                        {t.tripRef ? `${t.tripRef} — ` : ''}{t.title || 'Untitled trip'}
                        <span className="block text-xs text-gray-600">
                          {t.startDate || 'No date'} · {t.status}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div role="alert" className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={15} aria-hidden="true" className="text-red-600 mt-0.5 shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setShowAdd(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={saving}
                className="px-5 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Add credit'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
